import { Component, EventEmitter, Output, signal, Input, OnInit, OnDestroy, SimpleChanges, OnChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { ApiService } from '@app/core/services/API/api-service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { ConfirmPopup } from '@app/shared/components/confirm-popup/confirm-popup';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ProjectsService } from '@app/pages/projects/projects.service';

interface Note {
  id: string;
  user: string;
  userImage?: string;
  date: string;
  title: string;
  type: 'links' | 'images' | 'document' | 'table';
  source?: 'projects' | 'updates' | 'tasks' | 'documents' | 'requests' |'issues' |
  'revisions';
  objCode?: string; // Original object code from API
  objID?: string;
  content: {
    greeting?: string;
    url?: string;
    docUrl?: string;
    bulletPoints?: string[];
    images?: string[];
    fileName?: string;
    tableText?: string;
    tableHeaders?: string[];
    tableData?: string[][];
    textHTML?: string;
    docServerURL?: string;
    docName?: string;
    docSize?: string;
    docFormat?: string;
  };
}
interface Portfolio {
  _id: string;
  portfolioManager: {
    _id: string;
    name: string;
    image: string;
  };
  portfolioManagerID: string;
  portfolioName: string;
  portfolioDescription: string;
  isActive: boolean;
  status: string;
  createdBy: {
    _id: string;
    name: string;
    image: string;
  };
  lastUpdatedBy: {
    _id: string;
    name: string;
    image: string;
  };
  companyName: string;
  companyID: number;
  isElite: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  email: string;
  phone: string;
}
// API Response interface
interface ApiNote {
  _id: string;
  status: string;
  objCode: string;
  objID: string;
  projectID: string;
  portfolioID: string;
  pinnedBy: {
    _id: string;
    userID: string;
    name: string;
    email: string;
    utype: string;
    accesslevelID: string;
    accesslevelType: string;
    accesslevel_name: string;
    homeTeamID: string;
    agencyID: string;
    agencyDbID: string;
    en_id: string;
    jobInfo: string;
    image: string;
    timezone: string;
    managerID: string;
    accessor: string;
  };
  createdAt: string;
  updatedAt: string;
  __v: number;
  objData: {
    _id: string;
    status: string;
    isSystemUpdate?: boolean;
    textHTML?: string;
    notify?: string[];
    objCode: string;
    objID: string;
    projectID: string;
    parentID?: string;
    isReply?: boolean;
    createdBy?: {
      _id: string;
      userID: string;
      name: string;
      email: string;
      utype: string;
      accesslevelID: string;
      accesslevelType: string;
      accesslevel_name: string;
      homeTeamID: string;
      agencyID: string;
      agencyDbID: string;
      en_id: string;
      jobInfo: string;
      image: string;
      timezone: string;
      managerID: string;
      accessor: string;
    };
    // Document specific fields
    docName?: string;
    docSize?: string;
    docFormat?: string;
    docServerURL?: string;
    uploadedBy?: {
      _id: string;
      userID: string;
      name: string;
      email: string;
      utype: string;
      accesslevelID: string;
      accesslevelType: string;
      accesslevel_name: string;
      homeTeamID: string;
      agencyID: string;
      agencyDbID: string;
      en_id: string;
      jobInfo: string;
      image: string;
      timezone: string;
      managerID: string;
      accessor: string;
    };
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
  // Additional fields for project and portfolio data
  projectData?: any[];
  portfolioData?: any[];
}

@Component({
  selector: 'app-client-summary-slider',
  standalone: true,
  imports: [
    CommonModule, 
    MatIconModule, 
    FormsModule,
    MatButtonModule,
    MatTooltipModule,
    NgSelectModule,
    Spinner,
    ConfirmPopup
  ],
  templateUrl: './client-summary-slider.html',
  styleUrls: ['./client-summary-slider.scss']
})
export class ClientSummarySlider implements OnInit, OnDestroy,OnChanges {
  @Output() close = new EventEmitter<void>();
  @Input() context: { objCode: string, objID: string } = { objCode: 'CLIENT', objID: '' };

  // Dropdown state
  activeDropdownId = signal<string | null>(null);
  
  // API data
  clientSummaryData = signal<any>(null);
  isLoading = signal<boolean>(false);
  showConfirmPopup = signal<boolean>(false);
  noteToRemove = signal<string | null>(null);
  isRemoving = signal<boolean>(false);
  portfolios = signal<Portfolio[]>([]);
  selectedPortfolio = signal<Portfolio | null>(null);
  isLoadingPortfolios = signal<boolean>(false);
  noDataFound = signal<boolean>(false);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  departments = signal<any[]>([]);
  selectedDepartment = signal<any>(null);
  isLoadingDepartments = signal<boolean>(false);


  private subscription = new Subscription();
  private hasLoaded = false; // Flag to prevent multiple loads
  private currentContext: { objCode: string, objID: string } | null = null;
  private destroy$ = new Subject<void>();
  private projectsService = inject(ProjectsService);

 



  // Sort options
  sortOptions = [
    { id: 1, name: 'Date (Newest First)' },
    { id: 2, name: 'Date (Oldest First)' },
    { id: 3, name: 'User Name (A-Z)' },
    { id: 4, name: 'User Name (Z-A)' },
    { id: 5, name: 'Title' }
  ];
  
  selectedSort = this.sortOptions[0]; // Default to Date (Newest First)

  // Search functionality
  searchQuery = signal<string>('');
  filteredNotes = signal<Note[]>([]);

  constructor(private apiService: ApiService,private toastr: ToastrService, private router: Router) {}

  ngOnInit(): void {
    this.getPortfolio();
    this.checkAndLoadData();
    this.loadDepartments();
  }
  ngOnChanges(changes: SimpleChanges): void {
    // Check if context has changed and reload if necessary
    if (changes['context'] && !changes['context'].firstChange) {
      this.checkAndLoadData();
    }
  }

  private checkAndLoadData(): void {
    if (this.context.objID && this.context.objID !== 'default-client-id') {
      // Check if context has changed or if we haven't loaded yet
      const contextChanged = !this.currentContext || 
        this.currentContext.objCode !== this.context.objCode || 
        this.currentContext.objID !== this.context.objID;
      
      if (contextChanged || !this.hasLoaded) {
        this.currentContext = { ...this.context };
        this.loadClientSummary();
      }
    } else {
      // Show message that no valid context is provided
      console.warn('No valid context provided for client summary');
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  getPortfolio(): void {
    this.apiService.getPortfolios().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (response.success && response.data && Array.isArray(response.data)) {
          this.portfolios.set(response.data);
          // Set the first portfolio as selected by default
        // Don't auto-select first portfolio - let user choose
        this.selectedPortfolio.set(null);
        } else {
          console.warn('No portfolios data received or invalid format');
          this.toastr.warning('No portfolios available');
        }
        this.isLoadingPortfolios.set(false);
      },
      error: (error) => {
        console.error('Error loading portfolios:', error);
        this.toastr.error('Failed to load portfolios');
        this.isLoadingPortfolios.set(false);
      }
    });
  }
  loadDepartments(): void {
    this.isLoadingDepartments.set(true);
    
    this.projectsService.getProjectPhase().pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        console.log('Project phases response:', response);
        if (response && response.data && Array.isArray(response.data)) {
          // Map the API response to department format
          const departmentOptions = response.data.map((phase: any) => ({
            id: phase._id,
            name: phase.phase,
            value: phase._id
          }));
          
          // Add "All" option at the beginning
          const allOption = { id: 'all', name: 'All', value: 'all' };
          this.departments.set([allOption, ...departmentOptions]);
          
          // Set "All" as default selection
          this.selectedDepartment.set(allOption);
        } else {
          console.warn('No project phases data received or invalid format');
          // Fallback to "All" option only
          const allOption = { id: 'all', name: 'All', value: 'all' };
          this.departments.set([allOption]);
          this.selectedDepartment.set(allOption);
        }
        this.isLoadingDepartments.set(false);
      },
      error: (error) => {
        console.error('Error loading project phases:', error);
        this.toastr.error('Failed to load departments');
        // Fallback to "All" option only
        const allOption = { id: 'all', name: 'All', value: 'all' };
        this.departments.set([allOption]);
        this.selectedDepartment.set(allOption);
        this.isLoadingDepartments.set(false);
      }
    });
  }


  onPortfolioChange(portfolio: Portfolio): void {
    this.selectedPortfolio.set(portfolio);
    // Filter notes based on selected portfolio
    if (portfolio) {
      this.filterNotesByPortfolio(portfolio._id);
    } else {
      // If no portfolio selected, show all notes
      
    }
  }

  onDepartmentChange(department: any): void {
    this.selectedDepartment.set(department);
    console.log('Department changed to:', department);
    
    // Reload client summary data with department filter
    if (this.selectedPortfolio()) {
      this.loadClientSummaryForPortfolio(this.selectedPortfolio()!._id);
    } else if (this.context.objID && this.context.objID !== 'default-client-id') {
      this.loadClientSummary();
    }
  }
  onSortChange(sortOption: any): void {
    this.selectedSort = sortOption;
    this.applySorting();
  }

  // Search functionality
  onSearchChange(searchValue: string): void {
    this.searchQuery.set(searchValue);
    this.applySearchAndSort();
  }

  onSearchClear(): void {
    this.searchQuery.set('');
    this.applySearchAndSort();
  }

  private applySearchAndSort(): void {
    const currentNotes = this.notes();
    if (!currentNotes || currentNotes.length === 0) {
      this.filteredNotes.set([]);
      return;
    }

    let filteredNotes: Note[] = [...currentNotes];

    // Apply search filter
    const searchTerm = this.searchQuery().toLowerCase().trim();
    if (searchTerm) {
      filteredNotes = filteredNotes.filter(note => {
        const titleMatch = note.title.toLowerCase().includes(searchTerm);
        const userMatch = note.user.toLowerCase().includes(searchTerm);
        const contentMatch = note.content.textHTML && note.content.textHTML.toLowerCase().includes(searchTerm);
        const docNameMatch = note.content.docName && note.content.docName.toLowerCase().includes(searchTerm);
        const badgeTextMatch = this.getBadgeText(note).toLowerCase().includes(searchTerm);
        const sourceMatch = note.source && note.source.toLowerCase().includes(searchTerm);
        
        // Also search in other content fields
        const greetingMatch = note.content.greeting && note.content.greeting.toLowerCase().includes(searchTerm);
        const urlMatch = note.content.url && note.content.url.toLowerCase().includes(searchTerm);
        const docUrlMatch = note.content.docUrl && note.content.docUrl.toLowerCase().includes(searchTerm);
        const fileNameMatch = note.content.fileName && note.content.fileName.toLowerCase().includes(searchTerm);
        
        return titleMatch || userMatch || contentMatch || docNameMatch || badgeTextMatch || sourceMatch || 
               greetingMatch || urlMatch || docUrlMatch || fileNameMatch;
      });
    }

    // Apply sorting
    this.applySortingToNotes(filteredNotes);
  }

  private applySortingToNotes(notesToSort: Note[]): void {
    let sortedNotes: Note[] = [...notesToSort];

    switch (this.selectedSort.id) {
      case 1: // Date (Newest First)
        sortedNotes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 2: // Date (Oldest First)
        sortedNotes.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case 3: // User Name (A-Z)
        sortedNotes.sort((a, b) => a.user.localeCompare(b.user));
        break;
      case 4: // User Name (Z-A)
        sortedNotes.sort((a, b) => b.user.localeCompare(a.user));
        break;
      case 5: // Title
        sortedNotes.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        // Default to newest first
        sortedNotes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
    }

    this.filteredNotes.set(sortedNotes);
  }

  applySorting(): void {
    this.applySearchAndSort();
  }

  filterNotesByPortfolio(portfolioId: string): void {
    if (!portfolioId) {
      console.warn('No portfolio ID provided for filtering');
      return;
    }

    // Load portfolio-specific client summary
    this.loadClientSummaryForPortfolio(portfolioId);
  }

  loadClientSummaryForPortfolio(portfolioId: string): void {
    if (!portfolioId) {
      console.warn('No portfolio ID provided for client summary');
      return;
    }

    // Prevent multiple simultaneous calls
    if (this.isLoading()) {
      console.log('Client summary is already loading, skipping duplicate call');
      return;
    }

    this.isLoading.set(true);
    
    // Clean up any existing subscription before making new call
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    const departmentParam = this.selectedDepartment()?.value || 'all';
    this.subscription.add(
      this.apiService.getClientSummary('PORTFOLIO', portfolioId,departmentParam).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.clientSummaryData.set(response.data);
            this.noDataFound.set(false);
            this.hasError.set(false);
            this.errorMessage.set('');
            // Process API response data
            if (Array.isArray(response.data)) {
              const mappedNotes = this.mapApiResponseToNotes(response.data);
              this.notes.set(mappedNotes);
              this.filteredNotes.set(mappedNotes);
              this.applySearchAndSort();
            } else if (response.data.notes && Array.isArray(response.data.notes)) {
              const mappedNotes = this.mapApiResponseToNotes(response.data.notes);
              this.notes.set(mappedNotes);
              this.filteredNotes.set(mappedNotes);
              this.applySearchAndSort();
            }
          } else if (response.success === false && response.message === "No summary data found") {
            // Handle no data found response
            this.clientSummaryData.set(null);
            this.notes.set([]);
            this.filteredNotes.set([]);
            this.noDataFound.set(true);
            console.log('No summary data found for this portfolio');
          } else {
            // Handle other unsuccessful responses
            this.clientSummaryData.set(null);
            this.notes.set([]);
            this.filteredNotes.set([]);
            this.noDataFound.set(false);
            console.warn('Unexpected response format:', response);
          }
          this.isLoading.set(false);
        },
        error: (error) => {
          this.clientSummaryData.set(null);
          this.notes.set([]);
          this.noDataFound.set(false);
          this.hasError.set(true);
          this.errorMessage.set('Failed to load portfolio summary');
          console.error('Error loading client summary for portfolio:', error);
          this.toastr.error('Failed to load portfolio summary');
          this.isLoading.set(false);
        }
      })
    );
  }
  loadClientSummary(): void {
    if (!this.context.objID || this.context.objID === 'default-client-id') {
      console.warn('No valid context provided for client summary');
      return;
    }

    // Prevent multiple simultaneous calls
    if (this.isLoading()) {
      console.log('Client summary is already loading, skipping duplicate call');
      return;
    }

    this.isLoading.set(true);
    this.hasLoaded = true; // Mark as loaded to prevent duplicate calls
    
    // Clean up any existing subscription before making new call
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    const departmentParam = this.selectedDepartment()?.value || 'all';
    this.subscription.add(
      this.apiService.getClientSummary(this.context.objCode, this.context.objID, departmentParam).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.clientSummaryData.set(response.data);
            this.noDataFound.set(false);
            this.hasError.set(false);
            this.errorMessage.set('');
            // Process API response data
            if (Array.isArray(response.data)) {
              const mappedNotes = this.mapApiResponseToNotes(response.data);
              this.notes.set(mappedNotes);
              this.filteredNotes.set(mappedNotes);
              this.applySearchAndSort();
            } else if (response.data.notes && Array.isArray(response.data.notes)) {
              const mappedNotes = this.mapApiResponseToNotes(response.data.notes);
              this.notes.set(mappedNotes);
              this.filteredNotes.set(mappedNotes);
              this.applySearchAndSort();
            }
          } else if (response.success === false && response.message === "No summary data found") {
            // Handle no data found response
            this.clientSummaryData.set(null);
            this.notes.set([]);
            this.filteredNotes.set([]);
            this.noDataFound.set(true);
            console.log('No summary data found for this client');
          } else {
            // Handle other unsuccessful responses
            this.clientSummaryData.set(null);
            this.notes.set([]);
            this.filteredNotes.set([]);
            this.noDataFound.set(false);
            console.warn('Unexpected response format:', response);
          }
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading client summary:', error);
          this.clientSummaryData.set(null);
          this.notes.set([]);
          this.noDataFound.set(false);
          this.hasError.set(true);
          this.errorMessage.set('Failed to load client summary');
          this.isLoading.set(false);
        }
      })
    );
  }

  // Map API response to display format
  private mapApiResponseToNotes(apiNotes: ApiNote[]): Note[] {
    return apiNotes.map(apiNote => {
      const objCode =  apiNote.objCode;
      const isDocument = objCode === 'DOCUMENT';
      
      return {
        id: apiNote._id,
        user: apiNote.objData?.createdBy?.name || apiNote.pinnedBy?.name || 'Unknown User',
        userImage:  apiNote.objData?.createdBy?.image || apiNote.pinnedBy?.image || '',
        date: this.formatDate(apiNote.objData?.createdAt || apiNote.createdAt),
        title: this.getDocumentTitle(apiNote),
        type: isDocument ? 'document' : 'links',
        source: this.mapObjCodeToSource(objCode),
        objCode: apiNote.objCode,
        objID: apiNote.objID, 
        content: {
          textHTML: isDocument ? '' : (apiNote.objData?.textHTML || ''),
          docServerURL: isDocument ? (apiNote.objData?.docServerURL || '') : '',
          docName: isDocument ? (apiNote.objData?.docName || '') : '',
          docSize: isDocument ? (apiNote.objData?.docSize || '') : '',
          docFormat: isDocument ? (apiNote.objData?.docFormat || '') : ''
        }
      };
    });
  }

  // Get appropriate title for document or other types
  private getDocumentTitle(apiNote: ApiNote): string {
    if (apiNote.projectData && apiNote.projectData.length > 0) {
      return apiNote.projectData[0].projectName || 'Untitled';
    }
    return apiNote.objData?.projectID || apiNote.projectID || 'Untitled';
  }

    // Map objCode to source for badge display
    private mapObjCodeToSource(objCode: string): 'projects' | 'updates' | 'tasks' | 'documents' | 'requests' |'issues' |'revisions' {
      switch (objCode) {
        case 'PROJECT':
          return 'projects';
        case 'UPDATE':
          return 'updates';
        case 'TASK':
          return 'tasks';
        case 'DOCUMENT':
          return 'documents';
        case 'REQUEST':
          return 'requests';
        case 'ISSUE':
            return 'issues';
        case 'REVISION':
            return 'revisions';
        default:
          return 'updates';
      }
    }
  
    // Format date for display
    private formatDate(dateString: string): string {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }

  // Method to refresh data when context changes
  refreshClientSummary(): void {
    this.loadClientSummary();
  }

  // Get the context type for display purposes
  getContextType(): string {
    if (this.context.objCode === 'PROJECT') {
      return 'Project';
    } else if (this.context.objCode === 'PORTFOLIO') {
      return 'Client';
    } else {
      return 'Client';
    }
  }

  // Get the context ID for display purposes
  getContextId(): string {
    return this.context.objID;
  }

  // Get the object code for debugging/display purposes
  getObjectCode(): string {
    return this.context.objCode;
  }

  // Sample data matching the image exactly
  notes = signal<Note[]>([ ]);

  // getUserIconClass(note: Note): string {
  //   // Match the exact colors from the image
  //   if (note.user === 'Aziz') return 'purple';
  //   if (note.user === 'McKenna' && note.type === 'images') return 'green';
  //   if (note.user === 'Bonnie') return 'green';
  //   if (note.user === 'McKenna' && note.type === 'table') return 'yellow';
  //   return 'purple'; // fallback
  // }

  // Get user initials for avatar placeholder
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Generate consistent avatar color based on user name
  getAvatarColor(name: string): string {
    if (!name) return '#6b7280';
    
    // Rich vibrant colors
    const colors = [
      '#8b5cf6', // Purple
      '#10b981', // Green
      '#f59e0b', // Yellow/Amber
      '#3b82f6', // Blue
      '#ef4444', // Red
      '#ec4899', // Pink
      '#14b8a6', // Teal
      '#f97316', // Orange
      '#6366f1', // Indigo
      '#a855f7', // Purple
      '#06b6d4', // Cyan
      '#84cc16', // Lime
    ];

    // Generate hash from name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';

    // Find and show the avatar placeholder
    const avatarContainer = img.closest('.user-avatar') as HTMLElement;
    if (avatarContainer) {
      const placeholder = avatarContainer.querySelector('.avatar-placeholder') as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    }
  }

  // Toggle dropdown for note options
  toggleNoteDropdown(event: Event, noteId: string): void {
    event.stopPropagation();
    
    if (this.activeDropdownId() === noteId) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(noteId);
      
      // Close dropdown when clicking outside
      setTimeout(() => {
        const closeDropdown = () => {
          this.activeDropdownId.set(null);
          document.removeEventListener('click', closeDropdown);
        };
        document.addEventListener('click', closeDropdown);
      }, 0);
    }
  }


  // Check if dropdown is active for a note
  isDropdownActive(noteId: string): boolean {
    return this.activeDropdownId() === noteId;
  }

  // Get badge text based on note source
  getBadgeText(note: Note): string {
    if (!note.source) return 'General';
    
    switch (note.source) {
      case 'projects':
        return 'Projects';
      case 'updates':
        return 'Updates';
      case 'tasks':
        return 'Tasks';
      case 'documents':
        return 'Documents';
      case 'requests':
          return 'Requests';
      case 'issues':
            return 'Issues';
      case 'revisions':
            return 'Revisions';
      default:
        return 'General';
    }
  }

  // Get badge CSS class based on note source
  getBadgeClass(note: Note): string {
    if (!note.source) return 'badge-general';
    
    switch (note.source) {
      case 'projects':
        return 'badge-projects';
      case 'updates':
        return 'badge-updates';
      case 'tasks':
        return 'badge-tasks';
      case 'documents':
        return 'badge-documents';
      default:
        return 'badge-general';
    }
  }

  onClose() {
    // Reset loading state when closing
    this.isLoading.set(false);
    this.hasLoaded = false;
    this.currentContext = null;
    this.noDataFound.set(false);
    this.hasError.set(false);
    this.errorMessage.set('');
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    this.close.emit();
  }

  removeFromClientSummary(noteId: string): void {
    this.noteToRemove.set(noteId);
    this.showConfirmPopup.set(true);
  }

  onConfirmRemove(): void {
    const noteId = this.noteToRemove();
    if (!noteId) return;

    this.isRemoving.set(true);
    
    this.subscription.add(
      this.apiService.removeFromClientSummary(noteId).subscribe({
        next: (response) => {
          if (response.success) {
            this.toastr.success(response.message || 'Client summary removed successfully');
            // Remove the note from the local array
            const currentNotes = this.notes();
            const updatedNotes = currentNotes.filter(note => note.id !== noteId);
            this.notes.set(updatedNotes);
            
            // Close the confirm popup
            this.showConfirmPopup.set(false);
            this.noteToRemove.set(null);
          }
          this.isRemoving.set(false);
        },
        error: (error) => {
          this.toastr.error(error.message || 'Failed to remove from client summary');
          console.error('Error removing from client summary:', error);
          this.isRemoving.set(false);
          // You might want to show an error message to the user
        }
      })
    );
  }

  onCancelRemove(): void {
    this.showConfirmPopup.set(false);
    this.noteToRemove.set(null);
  }

  onViewPinClick(note: Note): void {
    if (!note.objCode || !note.objID) {
      this.toastr.warning('Unable to determine the object type or ID for navigation');
      return;
    }

    try {
      let navigationPath: string[] = [];
      
      // Use the actual objCode and objID from the note instead of context
      switch (note.objCode) {
        case 'PROJECT':
          // Navigate to project updates
          navigationPath = ['/projects/project-details', note.objID, 'updates'];
          break;
          
        case 'TASK':
          // Navigate to task updates
          navigationPath = ['/task/task-details', note.objID, 'updates'];
          break;
          
        case 'ISSUE':
          // Navigate to issue updates
          navigationPath = ['/issue/issue-details', note.objID, 'updates'];
          break;
          
        case 'REVISION':
          // Navigate to revision updates
          navigationPath = ['/revision/revision-details', note.objID, 'updates'];
          break;
          
        case 'REQUEST':
          // Navigate to request updates
          navigationPath = ['/request/request-details', note.objID, 'updates'];
          break;
          
        case 'DOCUMENT':
          // For documents, navigate to project updates (since documents belong to projects)
          if (this.context.objID) {
            navigationPath = ['/projects/project-details', this.context.objID, 'updates'];
          } else {
            navigationPath = ['/updates'];
          }
          break;
          
        case 'UPDATE':
          // For updates, navigate to project updates
          if (this.context.objID) {
            navigationPath = ['/projects/project-details', this.context.objID, 'updates'];
          } else {
            navigationPath = ['/updates'];
          }
          break;
          
        default:
          // Default to general updates page
          navigationPath = ['/updates'];
          break;
      }

      // Navigate to the determined path
      this.router.navigate(navigationPath);
      
      // Close the client summary slider after navigation
      this.onClose();
      
    } catch (error) {
      console.error('Error navigating to updates page:', error);
      this.toastr.error('Failed to navigate to updates page');
    }
  }
}
